import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "../../components/layouts/MainLayout";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import CancelModal from "../../components/subscription/CancelModal";
import AutoRenewToggle from "../../components/subscription/AutoRenewToggle";
import Button from "../../components/common/Button";
import { useToast } from "../../components/common/Toast";
import { useAuth } from "../../hooks/useAuth";
import { subscriptionService } from "../../services/subscription.service";
import { openRazorpayCheckout } from "../../utils/razorpayCheckout";
import { theme } from "../../config/theme";

const PLANS = {
  free: {
    label: "Starter",
    price: 0,
    unit: "forever free",
    color: "#64748b",
    gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
    popular: false,
    features: [
      "1 product listing",
      "PAN India visibility",
      "Basic analytics",
      "Email support",
    ],
  },
  monthly: {
    label: "Monthly",
    price: 359,
    unit: "per month",
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    popular: false,
    features: [
      "Unlimited product listings",
      "Priority search ranking",
      "Advanced analytics dashboard",
      "Email & chat support",
      "Bulk product upload",
      "Auto-renew subscription",
    ],
  },
  yearly: {
    label: "Yearly",
    price: 1799,
    unit: "per year — save ₹2,509",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
    popular: true,
    features: [
      "Everything in Monthly",
      "Top priority listing",
      "Dedicated account manager",
      "Advanced analytics + exports",
      "2 months free (vs monthly)",
      "API access",
    ],
  },
};

const STATUS_STYLES = {
  active: { bg: "#dcfce7", color: "#16a34a", label: "Active" },
  past_due: { bg: "#fef3c7", color: "#d97706", label: "Payment Due" },
  cancelled: { bg: "#fee2e2", color: "#dc2626", label: "Cancelled" },
  expired: { bg: "#f1f5f9", color: "#64748b", label: "Expired" },
  pending: { bg: "#ede9fe", color: "#7c3aed", label: "Pending" },
};

// ─── Sub-components ────────────────────────────────────────────────────────────
const PlanCard = ({ tier, currentTier, status, loading, onSelect, hasActiveSubscription }) => {
  const plan = PLANS[tier];
  const price = plan.price;
  const unit = plan.unit;
  const isCurrent = tier === currentTier && status === "active";
  const isBlocked = hasActiveSubscription && !isCurrent;

  const getButtonLabel = () => {
    if (loading) return "Processing…";
    if (isCurrent) return "✓ Current Plan";
    if (isBlocked) return "Cancel current plan first";
    if (tier === "free") return "Get Started Free";
    return "Subscribe Now";
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: theme.borderRadius.xl,
        overflow: "hidden",
        border: `2px solid ${isCurrent ? plan.color : "transparent"}`,
        background: isCurrent
          ? `linear-gradient(white, white) padding-box, ${plan.gradient} border-box`
          : theme.colors.white,
        boxShadow: isCurrent
          ? `0 8px 32px ${plan.color}33`
          : "0 2px 12px rgba(0,0,0,0.06)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        cursor: "pointer",
        flex: "1 1 280px",
        minWidth: "260px",
        maxWidth: "360px",
      }}
      onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 16px 48px ${plan.color}22`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isCurrent ? `0 8px 32px ${plan.color}33` : "0 2px 12px rgba(0,0,0,0.06)"; }}
    >
      {/* Popular badge */}
      {plan.popular && (
        <div style={{
          background: plan.gradient, color: "#fff",
          textAlign: "center", padding: `${theme.spacing.xs} 0`,
          fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.bold,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          ⭐ Most Popular
        </div>
      )}
      {tier === "yearly" && (
        <div style={{
          background: plan.gradient, color: "#fff",
          textAlign: "center", padding: `${theme.spacing.xs} 0`,
          fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.bold,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          🚀 Best Value — 2 Months Free
        </div>
      )}

      <div style={{ padding: theme.spacing.xl }}>
        {/* Header */}
        <div style={{ marginBottom: theme.spacing.lg }}>
          <p style={{
            fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.bold,
            textTransform: "uppercase", letterSpacing: "0.1em", color: plan.color, marginBottom: theme.spacing.xs
          }}>
            {plan.label}
          </p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
            <span style={{ fontSize: "36px", fontWeight: "800", color: theme.colors.text.primary, lineHeight: 1 }}>
              {price === 0 ? "Free" : `₹${price.toLocaleString("en-IN")}`}
            </span>
          </div>
          <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginTop: "4px" }}>
            {unit}
          </p>
        </div>

        {/* Features */}
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: theme.spacing.sm, marginBottom: theme.spacing.xl }}>
          {plan.features.map((f, i) => (
            <li key={i} style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              <span style={{
                width: "18px", height: "18px", borderRadius: "50%",
                background: `${plan.color}18`, color: plan.color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "10px", fontWeight: "bold", flexShrink: 0
              }}>✓</span>
              {f}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          disabled={isCurrent || isBlocked || loading}
          onClick={() => !isCurrent && !isBlocked && !loading && onSelect(tier)}
          style={{
            width: "100%",
            padding: `${theme.spacing.md} 0`,
            borderRadius: theme.borderRadius.lg,
            border: "none",
            cursor: isCurrent || isBlocked ? "default" : "pointer",
            background: isCurrent ? `${plan.color}20` : isBlocked ? theme.colors.gray[100] : plan.gradient,
            color: isCurrent ? plan.color : isBlocked ? theme.colors.text.secondary : "#fff",
            fontWeight: theme.typography.fontWeight.bold,
            fontSize: theme.typography.fontSize.sm,
            transition: "opacity 0.2s",
            opacity: loading ? 0.7 : 1,
          }}
          id={`plan-select-${tier}`}
        >
          {getButtonLabel()}
        </button>

        {!isCurrent && !isBlocked && tier !== "free" && (
          <p style={{ textAlign: "center", marginTop: theme.spacing.sm, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
            Auto-renews • Cancel anytime
          </p>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{
      backgroundColor: s.bg, color: s.color,
      padding: "3px 10px", borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
    }}>
      {s.label}
    </span>
  );
};

const CountdownBar = ({ endDate, status }) => {
  if (!endDate || status === "free" || status === "cancelled") return null;
  const daysLeft = Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000));
  const isUrgent = daysLeft <= 7;
  return (
    <div style={{ fontSize: theme.typography.fontSize.sm, color: isUrgent ? theme.colors.error : theme.colors.text.secondary }}>
      {daysLeft === 0 ? "Expires today" : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await subscriptionService.getCurrent();
      setData(res.data);
      setAutoRenew(res.data?.seller?.auto_renew ?? true);
    } catch {
      toast.error("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const status = searchParams.get("status");
    if (status === "success") toast.success("Subscription activated successfully! 🎉");
    if (status === "failed") toast.error("Payment failed. Please try again.");
    fetchSubscription();
  }, []);

  const handleSelectTier = async (tier) => {
    setPurchasing(tier);
    try {
      // Free tier: call purchase API directly, no payment needed
      if (tier === "free") {
        await subscriptionService.purchase(tier, "cod");
        toast.success("Free plan activated! You can now list 1 product. 🎉");
        fetchSubscription();
        return;
      }

      // Paid tiers: use Razorpay auto-renew subscription
      const res = await subscriptionService.purchaseAutoRenew(tier);
      const { subscriptionId, key, subscription } = res.data;

      const paymentResponse = await openRazorpayCheckout({
        key,
        subscription_id: subscriptionId,
        name: "AlarcTrade",
        description: `${PLANS[tier].label} Plan — Auto-Renew Subscription`,
        prefill: {
          name: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          email: user?.email ?? "",
          contact: user?.phone ?? "",
        },
        color: PLANS[tier].color,
      });

      await subscriptionService.verifyPayment({
        is_auto_renew: true,
        razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        subscription_id: subscription.id,
      });

      toast.success("Subscription activated! Welcome to " + PLANS[tier].label + " 🎉");
      fetchSubscription();
    } catch (err) {
      if (err?.message === "Payment cancelled by user") return;
      toast.error(err?.response?.data?.message || "Purchase failed. Please try again.");
    } finally {
      setPurchasing(null);
    }
  };

  const handleCancel = async (reason) => {
    setCancelLoading(true);
    try {
      await subscriptionService.cancel(reason);
      toast.success("Subscription cancelled. You'll keep access until the end of the billing period.");
      setShowCancelModal(false);
      fetchSubscription();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Cancellation failed.");
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;

  const { seller, subscription } = data || {};
  const isActive = seller?.subscription_status === "active";
  const isPastDue = seller?.subscription_status === "past_due" || subscription?.status === "past_due";
  const canCancel = (isActive || isPastDue) && seller?.tier !== "free";
  const hasRazorpay = !!subscription?.razorpay_subscription_id;

  const daysLeft = seller?.subscription_end_date
    ? Math.max(0, Math.ceil((new Date(seller.subscription_end_date) - new Date()) / 86400000))
    : null;

  return (
    <>
      <MainLayout>
        <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>

          {/* ── Hero Header ─────────────────────────────────────────────── */}
          <div style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
            padding: `${theme.spacing["2xl"]} ${theme.spacing.xl}`,
            color: "#fff",
          }}>
            <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: theme.spacing.md }}>
                <div>
                  <p style={{ color: "#a5b4fc", fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: theme.spacing.xs }}>
                    Seller Plans
                  </p>
                  <h1 style={{ fontSize: "clamp(24px, 4vw, 40px)", fontWeight: "800", letterSpacing: "-0.02em" }}>
                    Scale your business
                  </h1>
                  <p style={{ color: "#cbd5e1", marginTop: theme.spacing.sm, fontSize: theme.typography.fontSize.sm }}>
                    Flexible plans. Cancel anytime. Auto-renew included.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/seller/subscription/history")}
                  style={{
                    background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                    color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                    borderRadius: theme.borderRadius.lg, cursor: "pointer",
                    fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium,
                    backdropFilter: "blur(8px)",
                  }}
                >
                  View History →
                </button>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: theme.spacing.xl }}>

            {/* ── Current Plan Card ───────────────────────────────────────── */}
            {seller && seller.tier !== "free" && (
              <div style={{
                background: "#fff", borderRadius: theme.borderRadius.xl,
                border: `1px solid ${theme.colors.border.light}`,
                padding: theme.spacing.xl, marginBottom: theme.spacing["2xl"],
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: theme.spacing.lg }}>
                  {/* Left: Plan info */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                      <span style={{
                        fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
                        letterSpacing: "0.1em", color: PLANS[seller.tier]?.color || "#6366f1"
                      }}>
                        Current Plan
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.md, flexWrap: "wrap" }}>
                      <span style={{ fontSize: "22px", fontWeight: "800" }}>
                        {PLANS[seller.tier]?.label || seller.tier} Plan
                      </span>
                      <StatusBadge status={seller.subscription_status} />
                    </div>

                    {/* Expiry info */}
                    <div style={{ marginTop: theme.spacing.md, display: "flex", gap: theme.spacing.xl, flexWrap: "wrap" }}>
                      {seller.subscription_end_date && (
                        <div>
                          <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: "2px" }}>
                            {autoRenew ? "Renews on" : "Expires on"}
                          </p>
                          <p style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold }}>
                            {new Date(seller.subscription_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                        </div>
                      )}
                      {daysLeft !== null && (
                        <div>
                          <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: "2px" }}>Time remaining</p>
                          <CountdownBar endDate={seller.subscription_end_date} status={seller.subscription_status} />
                        </div>
                      )}
                      {subscription?.next_billing_date && autoRenew && (
                        <div>
                          <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: "2px" }}>Next billing</p>
                          <p style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold }}>
                            {new Date(subscription.next_billing_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Past due warning */}
                    {isPastDue && subscription && (
                      <div style={{
                        marginTop: theme.spacing.md, backgroundColor: "#fef3c7",
                        border: "1px solid #fbbf24", borderRadius: theme.borderRadius.md,
                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                        fontSize: theme.typography.fontSize.sm, color: "#92400e",
                      }}>
                        ⚠️ Payment failed. Retry attempt {subscription.retry_count}/3.
                        {subscription.next_retry_date && (
                          <> Next retry on {new Date(subscription.next_retry_date).toLocaleDateString("en-IN")}.</>
                        )}
                      </div>
                    )}

                    {/* Product usage */}
                    {seller.product_limit > 0 && (
                      <div style={{ marginTop: theme.spacing.md, maxWidth: "320px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: "4px" }}>
                          <span>Product slots</span>
                          <span>{seller.products_used} / {seller.product_limit}</span>
                        </div>
                        <div style={{ height: "6px", background: theme.colors.gray[200], borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{
                            height: "100%", borderRadius: "3px", transition: "width 0.4s ease",
                            width: `${Math.min(100, Math.round((seller.products_used / seller.product_limit) * 100))}%`,
                            background: (seller.products_used / seller.product_limit) > 0.9 ? theme.colors.error : PLANS[seller.tier]?.color || "#6366f1"
                          }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Auto-renew toggle */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: theme.spacing.md }}>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: "6px" }}>
                        Auto-Renew
                      </p>
                      <AutoRenewToggle
                        enabled={autoRenew}
                        disabled={!hasRazorpay}
                        onChange={(val) => setAutoRenew(val)}
                      />
                      {!hasRazorpay && (
                        <p style={{ fontSize: "10px", color: theme.colors.text.secondary, marginTop: "4px" }}>
                          Only available for Razorpay subscriptions
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Plan Cards ───────────────────────────────────────────────── */}
            <div
              style={{
                display: "flex", gap: theme.spacing.xl, flexWrap: "wrap",
                justifyContent: "center", marginBottom: theme.spacing["2xl"],
              }}
            >
              {["free", "monthly", "yearly"].map((tier) => (
                <PlanCard
                  key={tier}
                  tier={tier}
                  currentTier={seller?.tier}
                  status={seller?.subscription_status}
                  loading={purchasing === tier}
                  onSelect={handleSelectTier}
                  hasActiveSubscription={isActive && seller?.tier !== 'free'}
                />
              ))}
            </div>

            {/* ── Features Comparison ──────────────────────────────────────── */}
            <div style={{
              background: "#fff", borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              padding: theme.spacing.xl, marginBottom: theme.spacing["2xl"],
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}>
              <h2 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.lg, textAlign: "center" }}>
                Compare Plans
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: theme.typography.fontSize.sm }}>
                  <thead>
                    <tr>
                      {["Feature", "Starter", "Monthly", "Yearly"].map((h, i) => (
                        <th key={h} style={{
                          padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                          textAlign: i === 0 ? "left" : "center",
                          color: theme.colors.text.secondary,
                          fontWeight: theme.typography.fontWeight.semibold,
                          borderBottom: `2px solid ${theme.colors.border.light}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Product listings", "1", "Unlimited", "Unlimited"],
                      ["Priority search ranking", "—", "✓", "Top priority"],
                      ["Analytics dashboard", "Basic", "Advanced", "Advanced + Export"],
                      ["Support", "Email", "Email & Chat", "Dedicated manager"],
                      ["Auto-renew", "—", "✓", "✓"],
                      ["API access", "—", "—", "✓"],
                      ["Bulk upload", "—", "✓", "✓"],
                    ].map(([feat, ...vals], ri) => (
                      <tr key={feat} style={{ backgroundColor: ri % 2 === 0 ? "#f8fafc" : "#fff" }}>
                        <td style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontWeight: theme.typography.fontWeight.medium }}>{feat}</td>
                        {vals.map((v, vi) => (
                          <td key={vi} style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            textAlign: "center",
                            color: v === "—" ? theme.colors.gray[300] : v === "✓" ? "#16a34a" : theme.colors.text.primary,
                          }}>{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Cancel Subscription ──────────────────────────────────────── */}
            {canCancel && (
              <div style={{
                borderTop: `1px solid ${theme.colors.border.light}`,
                paddingTop: theme.spacing.xl, textAlign: "center",
              }}>
                <p style={{
                  fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.md
                }}>
                  Your plan stays active until the end of the billing period after cancellation.
                </p>
                <Button
                  variant="ghost"
                  style={{ color: theme.colors.error }}
                  onClick={() => setShowCancelModal(true)}
                  id="cancel-subscription-btn"
                >
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>
        </div>
      </MainLayout>

      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        loading={cancelLoading}
      />
    </>
  );
};

export default Subscription;